import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Button from '@mui/material/Button';
import withStyles from '@mui/styles/withStyles';
import TableImport from 'components/TableImport';
import ResultDialog from 'components/ResultDialog';
import { useMessage } from 'studio/components/Message';
import { useLoader } from 'components/loaders/FadeLoader';
import { trackEvent } from 'utils/eventTracking';
import styles from './Import.styles';
import Typography from '@mui/material/Typography';
import { studioRoutes } from 'components/app/routes';
import PageTitle from 'studio/components/PageTitle';

const ImportTables = ({ classes, connector, knowledgeGraph, setStep, editorRef, allowBack }) => {
  const history = useHistory();

  const [importFunction, setImportFunction] = useState(undefined);
  const [uploadResult, setUploadResult] = useState(null);

  const { successMessage, errorMessage } = useMessage();
  const { withLoader, loading } = useLoader();

  const importTables = useCallback(async () => {
    const response = await withLoader(importFunction(), 'Analyzing tables...');
    if (response.success) {
      if (response.data.messages.some(item => item.isSuccess)) {
        trackEvent('Data Imported', { knowledgeGraph: { id: knowledgeGraph.id } });
        successMessage('New Knowledge Graph created successfully!');
        history.push(`${studioRoutes.configuration}?id=${knowledgeGraph.id}`);
      }
      setUploadResult(response.data);
      // reload the editor's object and file trees
      editorRef.current.reloadTrees();
      return;
    }
    const message = response?.response?.data?.data?.message || 'An error occurred while analyzing tables.';
    trackEvent('Data Import Failed', { knowledgeGraph: { id: knowledgeGraph.id }, error: { message } });
    errorMessage(message);
  }, [importFunction, knowledgeGraph, successMessage, errorMessage, withLoader]);

  const handleFetchingError = useCallback((response, message) => errorMessage(message), [errorMessage]);

  return (
    <>
      <div className={classes.importContainer}>
        <PageTitle>3 - Select Data</PageTitle>
        <Typography variant="body1" gutterBottom>
          Select the tables and columns you want to ask questions about.
        </Typography>
        <TableImport
          usesSchemas={connector.usesSchemas}
          disabled={loading}
          knowledgeGraph={knowledgeGraph}
          setImportFunction={setImportFunction}
          handleFetchingError={handleFetchingError}
          dataTest="dataSelector"
        />
      </div>
      <div className={classes.actions}>
        {allowBack && (
          <Button type="button" disabled={loading} onClick={() => setStep(step => step - 1)}>
            Back
          </Button>
        )}
        <Button
          disabled={loading || importFunction === undefined}
          variant="contained"
          color="primary"
          onClick={importTables}
        >
          Import
        </Button>
      </div>
      <ResultDialog uploadResult={uploadResult} handleClose={() => setUploadResult(null)} />
    </>
  );
};

export default withStyles(styles)(ImportTables);
